import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "cartonomie_cookie_consent_v1";

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) setVisible(true);
  }, []);

  const save = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ analytics: choice === "all", ts: Date.now() })
      );
    } catch {
      /* ignore */
    }
    setVisible(false);
    // Reload only if user accepted analytics — to inject plausible if needed
    if (choice === "all") {
      window.dispatchEvent(new Event("cookie-consent-granted"));
    }
  };

  if (!visible) return null;

  const isFr = (i18n.language || "en").toLowerCase().startsWith("fr");

  const copy = isFr
    ? {
        title: "🍪 Vos préférences cookies",
        body:
          "Cartonomie utilise des cookies strictement nécessaires au fonctionnement du site. Aucun cookie publicitaire, aucun traceur tiers, aucune mesure d'audience tierce — nos statistiques sont calculées en interne sur des données anonymisées.",
        link: "En savoir plus",
        accept: "Tout accepter",
        reject: "Refuser",
      }
    : {
        title: "🍪 Cookie preferences",
        body:
          "Cartonomie uses strictly necessary cookies for the site to function. No advertising cookies, no third-party trackers, no third-party analytics — audience metrics are computed internally on anonymized data.",
        link: "Learn more",
        accept: "Accept all",
        reject: "Decline",
      };

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[60] bg-white border border-slate-200 rounded-2xl shadow-2xl p-5 animate-in slide-in-from-bottom-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
          <Cookie className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-sm text-slate-900 mb-1">
            {copy.title}
          </h3>
          <p className="text-xs text-slate-600 leading-relaxed">
            {copy.body}{" "}
            <Link
              to="/cookies"
              className="text-[#E11D48] font-semibold underline underline-offset-2"
              onClick={() => setVisible(false)}
            >
              {copy.link}
            </Link>
            .
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              data-testid="cookie-reject-btn"
              onClick={() => save("essential")}
              className="px-3 py-1.5 text-xs font-semibold rounded-xl border border-slate-300 text-slate-700 hover:border-slate-400 transition"
            >
              {copy.reject}
            </button>
            <button
              data-testid="cookie-accept-btn"
              onClick={() => save("all")}
              className="px-4 py-1.5 text-xs font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition"
            >
              {copy.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
